// Saga для IntegrationsPage
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  addNetworkToIntegration,
  createIntegration,
  getIntegrationByCode,
  getIntegrations,
  removeNetworkFromIntegration,
  setNetworkActive,
  updateIntegrationInfo,
} from './actions';
import { IntegrationsService } from './service';
import {
  BaseResponse,
  Integration,
  GetIntegrationByCodeRequestPayload,
  CreateIntegrationRequestPayload,
  ChangeNetworkCodeRequestPayload,
  SetNetworkCodeActiveRequestPayload,
  UpdateIntegrationInfoRequestPayload,
} from './types';

// Загрузить все интеграции
function* getIntegrationsSaga() {
  try {
    const data: Integration[] = yield call(IntegrationsService.getIntegrations);
    yield put(getIntegrations.success(data));
  } catch (e: any) {
    yield put(
      getIntegrations.error(e?.message || 'Failed to load integrations')
    );
  }
}

// Загрузить интеграцию по коду
function* getIntegrationByCodeSaga(action: {
  type: string;
  payload: GetIntegrationByCodeRequestPayload;
}) {
  try {
    const data: Integration = yield call(
      IntegrationsService.getIntegrationByCode,
      action.payload.code
    );
    yield put(getIntegrationByCode.success(data));
  } catch (e: any) {
    yield put(
      getIntegrationByCode.error(e?.message || 'Failed to load integration')
    );
  }
}

// Создать интеграцию
function* createIntegrationSaga(action: {
  type: string;
  payload: CreateIntegrationRequestPayload;
}) {
  try {
    const response: BaseResponse = yield call(
      IntegrationsService.createIntegration,
      {
        code: action.payload.code,
        network_codes: action.payload.network_codes,
      }
    );
    if (response.status === 'error') {
      throw new Error(response.error || 'Failed to create integration');
    }
    // Обновляем список интеграций
    yield put(getIntegrations.request());
    if (action.payload.postEffect) {
      action.payload.postEffect();
    }
    yield put(createIntegration.success(response));
  } catch (e: any) {
    yield put(createIntegration.error(e?.message || 'Failed to create'));
  }
}

// Привязать сеть
function* addNetworkSaga(action: {
  type: string;
  payload: ChangeNetworkCodeRequestPayload;
}) {
  try {
    const response: BaseResponse = yield call(
      IntegrationsService.addNetwork,
      action.payload
    );
    if (response.status === 'error') {
      throw new Error(response.error || 'Failed to add network');
    }
    // После мутации перезагружаем выбранную интеграцию
    yield put(
      getIntegrationByCode.request({ code: action.payload.integration_code })
    );
    if (action.payload.postEffect) {
      action.payload.postEffect();
    }
    yield put(addNetworkToIntegration.success(response));
  } catch (e: any) {
    yield put(
      addNetworkToIntegration.error(e?.message || 'Failed to add network')
    );
  }
}

// Отвязать сеть
function* removeNetworkSaga(action: {
  type: string;
  payload: ChangeNetworkCodeRequestPayload;
}) {
  try {
    const response: BaseResponse = yield call(
      IntegrationsService.removeNetwork,
      action.payload
    );
    if (response.status === 'error') {
      throw new Error(response.error || 'Failed to remove network');
    }
    yield put(
      getIntegrationByCode.request({ code: action.payload.integration_code })
    );
    if (action.payload.postEffect) {
      action.payload.postEffect();
    }
    yield put(removeNetworkFromIntegration.success(response));
  } catch (e: any) {
    yield put(
      removeNetworkFromIntegration.error(
        e?.message || 'Failed to remove network'
      )
    );
  }
}

// Изменить активность сети
function* setNetworkActiveSaga(action: {
  type: string;
  payload: SetNetworkCodeActiveRequestPayload;
}) {
  try {
    const response: BaseResponse = yield call(
      IntegrationsService.setNetworkActive,
      action.payload
    );
    if (response.status === 'error') {
      throw new Error(response.error || 'Failed to change network status');
    }
    yield put(
      getIntegrationByCode.request({ code: action.payload.integration_code })
    );
    if (action.payload.postEffect) {
      action.payload.postEffect();
    }
    yield put(setNetworkActive.success(response));
  } catch (e: any) {
    yield put(
      setNetworkActive.error(e?.message || 'Failed to change network status')
    );
  }
}

// Обновить информацию об интеграции (front_url, sender)
function* updateIntegrationInfoSaga(action: {
  type: string;
  payload: UpdateIntegrationInfoRequestPayload;
}) {
  try {
    const response: BaseResponse = yield call(
      IntegrationsService.updateIntegrationInfo,
      action.payload
    );
    if (response.status === 'error') {
      throw new Error(response.error || 'Failed to update integration info');
    }
    yield put(
      getIntegrationByCode.request({ code: action.payload.integration_code })
    );
    if (action.payload.postEffect) {
      action.payload.postEffect();
    }
    yield put(updateIntegrationInfo.success(response));
  } catch (e: any) {
    yield put(
      updateIntegrationInfo.error(
        e?.message || 'Failed to update integration info'
      )
    );
  }
}

export function* integrationsWatcherSaga() {
  yield all([
    takeLatest(getIntegrations.REQUEST, getIntegrationsSaga),
    takeLatest(getIntegrationByCode.REQUEST, getIntegrationByCodeSaga),
    takeLatest(createIntegration.REQUEST, createIntegrationSaga),
    takeLatest(addNetworkToIntegration.REQUEST, addNetworkSaga),
    takeLatest(setNetworkActive.REQUEST, setNetworkActiveSaga),
    takeLatest(removeNetworkFromIntegration.REQUEST, removeNetworkSaga),
    takeLatest(updateIntegrationInfo.REQUEST, updateIntegrationInfoSaga),
  ]);
}
